import React, { FormEvent } from 'react'
import { useForm, Link } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ColorPicker } from '@/components/shared/ColorPicker'

interface Props {
  project: {
    id: number
    name: string
    slug: string
    description: string | null
    color: string | null
  }
}

export default function Edit({ project }: Props) {
  const { data, setData, put, processing, errors } = useForm({
    project: {
      name: project.name,
      description: project.description || '',
      color: project.color || '',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    put(`/manage/projects/${project.id}`)
  }

  return (
    <SidebarLayout section="manage" title={`Edit project — ${project.name}`}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 600,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={data.project.name}
            onChange={e => setData('project', { ...data.project, name: e.target.value })}
            error={'project.name' in errors}
            helperText={(errors as Record<string, string>)['project.name']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={data.project.description}
            onChange={e => setData('project', { ...data.project, description: e.target.value })}
            error={'project.description' in errors}
            helperText={(errors as Record<string, string>)['project.description'] || 'Optional description of this project'}
            sx={{ mb: 2.5 }}
          />
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Color</Typography>
            <ColorPicker
              value={data.project.color}
              onChange={(color) => setData('project', { ...data.project, color })}
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Update project
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/projects" style={{ color: '#1a1a1a' }}>Back to projects</Link>
          </Box>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
